import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { first } from 'rxjs';

import {
  CanvasElement,
  CanvasElementId,
  CanvasElementType,
  PartialPropsOf,
  PropsWithOverrides,
  RenderTarget,
  StyleConfigProps,
} from '@blaze-runtime/engine-domain';
import {
  CanvasElementWithDirtyFieldProps,
  ElementPropsSection,
  ElementPropsUpdate,
  PropsUpdate,
} from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { deepmergeExceptArrays, isDeepEqual } from '@blaze-shared/shared-util';
import { fromStyles } from '@blaze-shared/style-state-feature';

import { getElementById } from '../+state/elements';
import {
  mergeElementPropsByTarget,
  mergePropsByTarget,
} from '../canvas-elements/element-model/props-overrides';
import type { ParentChildrenOverrides } from '../canvas-elements/parent-overrides';

import { ElementDefinitionsService } from './element-definitions.service';
import { ElementStylesService } from './styles';

/**
 * Merged props of a styling parent, read element-type-agnostically: the children overrides
 * live under `content.children`/`style.children` of whichever element owns the resolver,
 * and `styleConfig` decides whether a stylesheet has to be merged under them first.
 */
type MergedParentProps = PartialPropsOf<CanvasElementType> & Partial<StyleConfigProps>;

/**
 * One application of a parent's children overrides onto one child. Both ids are named rather
 * than positional: they are the same type, and swapping them would silently style the parent
 * with its own child's bundle.
 */
interface ApplyParentOverridesRequest<T extends CanvasElementType> {
  /** The element whose children overrides are being applied. */
  parentId: CanvasElementId;
  /** The child receiving them - which bundle, if any, is the parent's answer to give. */
  childId: CanvasElementId;
  elementType: T;
  elementProps: PartialPropsOf<T>;
  patchInitProps: PartialPropsOf<T>;
  renderTarget?: RenderTarget;
}

@Injectable({ providedIn: 'root' })
export class ElementPropsService {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly store = inject(Store);
  private readonly elementStyleService = inject(ElementStylesService);

  /**
   * Render-time overlay of the parent's children overrides onto one child. The parent's
   * definition declares participation via `childrenOverridesResolver`; without it (or with
   * an empty bundle) the overlay is a no-op and `patchInitProps` is returned unchanged.
   */
  applyParentOverrides<T extends CanvasElementType>({
    parentId,
    childId,
    elementType,
    elementProps,
    patchInitProps,
    renderTarget = 'desktop',
  }: ApplyParentOverridesRequest<T>): PartialPropsOf<T> {
    const parentElement: CanvasElement | undefined = syncSelect(
      this.store,
      getElementById(parentId),
    );
    const overrides = this.resolveParentChildrenOverrides(parentElement, childId, renderTarget);
    if (!overrides?.content && !overrides?.style) {
      return patchInitProps;
    }

    const elementPropsBuilder = this.elementDefinitionsService.getPropsBuilder(
      elementType,
      patchInitProps,
    );

    const initial = this.elementDefinitionsService.getDefinition(elementType)?.initial;

    if (overrides.content) {
      elementPropsBuilder?.setParentContentProps(elementProps, overrides.content, initial);
    }
    if (overrides.style) {
      elementPropsBuilder?.setParentStyleProps(elementProps, overrides.style, initial);
    }

    return elementPropsBuilder?.build() ?? patchInitProps;
  }

  /**
   * The parent's children overrides bundle, resolved through the parent definition's
   * `childrenOverridesResolver`. Also used as a re-render trigger by observers of the
   * parent element (a child's own entity doesn't change when the parent styling does).
   */
  resolveParentChildrenOverrides(
    parentElement: CanvasElement | undefined,
    childId: CanvasElementId,
    renderTarget: RenderTarget = 'desktop',
  ): ParentChildrenOverrides | undefined {
    if (
      !parentElement ||
      !this.elementDefinitionsService.hasChildrenOverridesResolver(parentElement.type)
    ) {
      return undefined;
    }

    let parentProps: MergedParentProps | undefined = mergeElementPropsByTarget(
      parentElement,
      renderTarget,
    );
    const parentStyleConfig = parentProps?.styleConfig;

    // The parent's stylesheet values merge under its own props BEFORE the bundle is
    // extracted, so sheet-driven children overrides flow the same way panel-set ones do
    if (parentStyleConfig?.styleSheetId || parentStyleConfig?.styleName) {
      let sheetData: PropsWithOverrides | undefined;
      this.elementStyleService
        .observeElementStyles(
          parentStyleConfig?.styleSheetId,
          parentStyleConfig?.styleName,
          parentElement.type,
        )
        .pipe(first())
        .subscribe((s) => {
          sheetData = fromStyles.mapStyleDataToProps(s?.data ?? []);
        });

      // The sheet stream is store-backed and replays, so it lands before the read below;
      // the guard is what stops a future async source from turning that into a TypeError
      if (sheetData) {
        const sheetProps = mergePropsByTarget(sheetData.props, sheetData.overrides, renderTarget);
        // deepmergeExceptArrays is declared to return `unknown`; both inputs are this
        // element's props, so the merge is too
        parentProps = deepmergeExceptArrays(
          sheetProps ?? {},
          parentProps ?? {},
        ) as MergedParentProps;
      }
    }

    return this.elementDefinitionsService.getChildrenOverrides(
      parentElement.type,
      parentProps ?? {},
      childId,
    );
  }

  clearParentOverridableContentProps(
    elements: Dictionary<CanvasElement>,
    inputsIds: CanvasElementId[],
    renderTarget: RenderTarget,
  ): ElementPropsUpdate[] {
    return this.clearParentOverridableProps(elements, inputsIds, 'content', renderTarget);
  }
  clearParentOverridableStyleProps(
    elements: Dictionary<CanvasElement>,
    inputsIds: CanvasElementId[],
    renderTarget: RenderTarget,
  ): ElementPropsUpdate[] {
    return this.clearParentOverridableProps(elements, inputsIds, 'style', renderTarget);
  }

  private clearParentOverridableProps(
    elements: Dictionary<CanvasElement>,
    inputsIds: CanvasElementId[],
    section: ElementPropsSection,
    renderTarget: RenderTarget,
  ): ElementPropsUpdate[] {
    const result: ElementPropsUpdate[] = [];
    for (const elementId of inputsIds) {
      const element = elements[elementId];
      if (!element) {
        console.error('Missing element');
        continue;
      }
      const props = mergeElementPropsByTarget(element, renderTarget) ?? {};
      const elementPropsBuilder = this.elementDefinitionsService.getPropsBuilder(
        element.type,
        props,
      );
      if (!elementPropsBuilder) {
        console.error('Missing element props-builder');
        continue;
      }
      if (section === 'content') {
        elementPropsBuilder.setParentContentProps(props, null);
      } else {
        elementPropsBuilder.setParentStyleProps(props, null);
      }
      const patch = elementPropsBuilder.build();
      // A clear may span prop sections (e.g. textArea typography sits under content.font
      // while its color is style.general). The requested section is emitted always - its
      // breakpoint overrides must be wiped even when the desktop values didn't change -
      // the other one only when the clear actually changed it.
      const extraSection = section === 'content' ? 'style' : 'content';
      const updates: PropsUpdate[] = [{ path: section, value: (patch as any)?.[section] }];
      if (!isDeepEqual((patch as any)?.[extraSection], (props as any)?.[extraSection])) {
        updates.push({ path: extraSection, value: (patch as any)?.[extraSection] });
      }
      const dirtyPatch = patch as CanvasElementWithDirtyFieldProps;
      if (section === 'content' && 'dirty' in dirtyPatch) {
        updates.push({ path: 'dirty', value: dirtyPatch.dirty });
      }

      result.push({ elementId, updates });
    }

    return result;
  }
}
